"use client";

import { createContext, useContext, useLayoutEffect, useRef } from "react";

import { useWallet } from "@/hooks/use-wallet";

import type { ReactNode, RefObject } from "react";
import type { SolanaProvider } from "@/lib/mudwater";
import type { WalletState } from "@/lib/wallet/context";

export interface SolanaWalletSession {
  /** The address the run started with, and the only one it may ask. */
  owner: string;
  /** Null until the provider is ready; a run checks before it starts. */
  solana: SolanaProvider | null;
  /** Asked by the SDK immediately before every prompt of the run. */
  isCurrent: () => boolean;
}

type SessionWallet = Pick<
  WalletState,
  "connected" | "solanaAddress" | "solana" | "solanaWallet"
>;

/**
 * How long a session outlives its address going away. A different address
 * ends it at once; a gap ends it only once it has lasted this long, so a
 * wallet list that empties and refills (an iframe re-initialising, a session
 * refresh) does not stop a holder's run. Privy 3.46 does not do that today:
 * it empties the list only when the login itself ends. A real disconnect
 * inside the window still fails safe: the prompt has no wallet to open in.
 */
export const SESSION_GRACE_MS = 3_000;

/**
 * The Solana wallet a conversion run belongs to, captured when it starts.
 *
 * Ended by a different address, or by no address for longer than
 * `SESSION_GRACE_MS` (a Phantom disconnect, a Glyph sign-out). A Phantom
 * account switch is a live swap of the address under the same connection, so
 * the address is what is watched; the EVM side is no part of a conversion,
 * and switching it must not stop one. Nor does the provider object: it is
 * re-created on routine re-renders, and a brief null there is not the holder
 * leaving.
 *
 * `solana` is read live while the session holds, and as captured once it has
 * ended. A provider for the new account would sign with the new key; the
 * original one lets the SDK's own check before the prompt say what happened
 * instead.
 *
 * The returned ref holds the session a new run may start from: null while
 * there is no address, even inside the grace.
 */
function useTrackedSession({
  connected,
  solanaAddress,
  solana,
  solanaWallet,
}: SessionWallet) {
  const current = useRef<SolanaWalletSession | null>(null);
  const held = useRef<{
    session: SolanaWalletSession;
    wallet?: string;
    lostAt?: number;
  }>(null);
  const latest = useRef(solana);
  useLayoutEffect(() => {
    latest.current = solana;
  }, [solana]);
  // Synchronize the external SDK's signing lifetime with committed wallet changes.
  useLayoutEffect(() => {
    const address = connected ? solanaAddress : null;
    const kept = held.current;
    const within = (lostAt?: number) =>
      lostAt === undefined || Date.now() - lostAt <= SESSION_GRACE_MS;
    if (!address) {
      if (kept) kept.lostAt ??= Date.now();
      current.current = null;
      return;
    }
    // The same address in another wallet is another session: one recovery
    // phrase imported into both gives Phantom and MetaMask the same address,
    // and a run must not carry on in a wallet it did not start in.
    if (
      kept?.session.owner === address &&
      kept.wallet === solanaWallet &&
      within(kept.lostAt)
    ) {
      kept.lostAt = undefined;
      current.current = kept.session;
      return;
    }
    const captured = latest.current;
    const session: SolanaWalletSession = {
      owner: address,
      get solana() {
        return session.isCurrent() ? (latest.current ?? captured) : captured;
      },
      // Timestamps rather than a timer, so this stays synchronous and exact.
      isCurrent: () =>
        held.current?.session === session && within(held.current.lostAt),
    };
    held.current = { session, wallet: solanaWallet };
    current.current = session;
  }, [connected, solanaAddress, solanaWallet]);
  // Unmounting the tracker ends what it tracked: nothing watches the wallet
  // for it any more.
  useLayoutEffect(
    () => () => {
      held.current = null;
      current.current = null;
    },
    []
  );
  return current;
}

const SharedSession =
  createContext<RefObject<SolanaWalletSession | null> | null>(null);

/**
 * One session for the whole app, under the wallet bridge. Pages come and go
 * during a run (a holder leaves the inventory mid-return), and a session
 * tracked by the page would end with it and stop the run at its next prompt.
 */
export function SolanaSessionScope({ children }: { children: ReactNode }) {
  const session = useTrackedSession(useWallet());
  return <SharedSession value={session}>{children}</SharedSession>;
}

/** The app's session where the scope is mounted; tracked here otherwise. */
export function useSolanaWalletSession(wallet: SessionWallet) {
  const shared = useContext(SharedSession);
  const local = useTrackedSession(wallet);
  return shared ?? local;
}
